import { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Dialog,
  DialogContent,
  Typography,
  Slide,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { getcaptcha } from "../../services/captcha.api";
import { addCodeClient, getCodeClient } from "../../services/client.api";
import FingerprintJS from "@fingerprintjs/fingerprintjs";

// ✅ Slide Animation for Dialog
const Transition = Slide as React.ComponentType<
  TransitionProps & { children: React.ReactElement }
>;

const PromoCodeForm: React.FC = () => {
  const [form, setForm] = useState({
    username: "",
    code: "",
    captcha: "",
  });
  const [fp, setFp] = useState<string>("");
  const [captchaLink, setCaptchaLink] = useState({ captchaUrl: "", token: "" });

  const [errors, setErrors] = useState({
    username: false,
    code: false,
    captcha: false,
  });
  const [point, setPoint] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const [openSuccess, setOpenSuccess] = useState(false);
  const [openError, setOpenError] = useState(false);

  useEffect(() => {
    FingerprintJS.load()
      .then(fpAgent => fpAgent.get())
      .then(result => {
        setFp(result.visitorId);
      });
  }, []);

  // ✅ Change handler
  const handleChange = async (field: string, value: string) => {
    setForm({ ...form, [field]: value });
    setErrors({ ...errors, [field]: false });
  };
  // ✅ Submit form
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors = {
      username: !form.username,
      code: !form.code,
      captcha: !form.captcha,
    };
    setErrors(newErrors);
    const isValid = Object.values(newErrors).every((v) => !v);
    if (!isValid) {
      // กรอกไม่ครบ
      setErrorMessage("กรุณากรอกข้อมูลให้ครบถ้วน");
      setOpenError(true);
      return;
    }
    if (form.captcha) {
      try {
        setLoading(true);
        let codeResult = await getCodeClient(form.code, { captchaCode: form.captcha, token: captchaLink.token });
        console.log("codeResult: ", codeResult);
        if (codeResult?.status_code !== 200 || !codeResult?.valid) {
          throw new Error(codeResult?.title_mess?.th || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
        }
        let addPoint = await addCodeClient(form.code, form.username, fp);
        if (!addPoint.success) {
          // addPoint.text_mess[intl.locale]
          throw new Error(addPoint?.text_mess?.th || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
        } else {
          setPoint(addPoint?.data?.amount ?? addPoint?.data?.point ?? 0)
          setOpenSuccess(true);
        }
      } catch (error: any) {
        checkCaptcha();
        setErrorMessage(error.message);
        setForm({ username: "", code: "", captcha: "" });
        return setOpenError(true);
      } finally {
        setLoading(false);
      }
      //setOpenSuccess(true);
    } else {
      // ❌ ผิด
      setOpenError(true);
      setErrorMessage("รหัสแคปช่าไม่ถูกต้อง กรุณาลองใหม่อีกครั้ง");
      checkCaptcha();
      setForm({ ...form, captcha: "" });
    }
  };

  const handleCloseAll = () => {
    setOpenSuccess(false);
    setOpenError(false);
  };

  const checkCaptcha = async () => {
    const res = await getcaptcha();
    if (res) {
      setCaptchaLink(res as any)
    }
  }
  useEffect(() => {
    checkCaptcha()
  }, [])

  return (
    <>
      {/* ✅ Main Form */}
      {/* ✅ ชั้นนอก (กรอบ gradient เหลือง-ส้ม) */}
      <Box
        sx={{
          backgroundImage: 'url("/imgs/Group.png")', // พื้นหลังขอบเหลือง–ส้ม
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          borderRadius: "30px",
          p: 1.5, // ช่องว่างระหว่างขอบนอกกับข้างใน
          width: { xs: "100%", md: "580px" },
          mx: "auto",
          mt: { xs: -2, md: -4 }, // ✅ มือถือ mt = 2 (ลงนิดหน่อย), Desktop mt = -4.5
          boxShadow: `
          0 10px 25px rgba(255, 160, 0, 0.4),
          0 0 20px rgba(255, 200, 50, 0.3)
        `,
        }}
      >
        {/* ✅ ชั้นใน (พื้นเหลืองอ่อนด้านใน) */}
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            background: "linear-gradient(180deg, #FFFDE7 0%, #FFF9C4 100%)", // สีเหลืองอ่อน
            borderRadius: "22px",
            p: { xs: 3, md: 4 },
            boxShadow:
              "inset 0 2px 8px rgba(255,255,255,0.6), inset 0 -2px 8px rgba(255,200,0,0.25)",
          }}
        >
          {/* Username */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Box
              component="img"
              draggable="false"
              src="/imgs/user.png?v12"
              alt="user"
              sx={{ width: 28, height: 28, mr: 1.5 }}
            />
            <TextField
              fullWidth
              required
              placeholder="กรอกยูสเซอร์"
              variant="outlined"
              size="small"
              value={form.username}
              error={errors.username}
              helperText={errors.username && "กรุณากรอกยูสเซอร์"}
              onChange={(e) => handleChange("username", e.target.value)}
              autoComplete="off"
              sx={{
                bgcolor: "#fff",
                borderRadius: "8px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  fontSize: "1rem",
                },
              }}
            />
          </Box>

          {/* Code */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Box
              component="img"
              draggable="false"
              src="/imgs/Promotion.png"
              alt="code"
              sx={{ width: 28, height: 28, mr: 1.5 }}
            />
            <TextField
              fullWidth
              required
              placeholder="กรอกโค้ดฟรี"
              variant="outlined"
              size="small"
              value={form.code}
              error={errors.code}
              helperText={errors.code && "กรุณากรอกโค้ดฟรี"}
              onChange={(e) => handleChange("code", e.target.value)}
              autoComplete="off"
              sx={{
                bgcolor: "#fff",
                borderRadius: "8px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  fontSize: "1rem",
                },
              }}
            />
          </Box>

          {/* Captcha */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <Box
              component="img"
              draggable="false"
              src="/imgs/Captcha.png"
              alt="captcha"
              sx={{ width: 28, height: 28, mr: 1.5 }}
            />
            <Box sx={{ display: "flex", flex: 1 }}>
              <Box
                onClick={checkCaptcha}
                sx={{
                  width: 90,
                  height: 40,
                  borderRadius: "8px",
                  mr: 1.5,
                  background:
                    "linear-gradient(90deg, #FFCDD2 0%, #F8BBD0 50%, #FFCDD2 100%)",
                  boxShadow: "inset 0 0 5px rgba(255,0,0,0.3)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  color: "#D32F2F",
                  fontWeight: "bold",
                  fontSize: "1.2rem",
                  cursor: "pointer",
                }}
              >
                <Box
                  component="img"
                  src={captchaLink.captchaUrl as unknown as string}
                  alt="captcha"
                  sx={{
                    width: 100,
                    height: "auto",
                    borderRadius: 1,
                    border: "1px solid rgba(255,255,255,0.2)",
                    display: "block",
                  }}
                />
              </Box>
              <TextField
                fullWidth
                required
                placeholder="กรอกเลขยืนยัน"
                variant="outlined"
                size="small"
                value={form.captcha}
                error={errors.captcha}
                helperText={errors.captcha && "กรุณากรอกเลขยืนยัน"}
                onChange={(e) => handleChange("captcha", e.target.value)}
                autoComplete="off"
                sx={{
                  bgcolor: "#fff",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontSize: "1rem",
                  },
                }}
              />
            </Box>
          </Box>

          {/* Submit Button */}
          <Button
            fullWidth
            type="submit"
            variant="contained"
            loading={loading}
            sx={{
              background:
                "linear-gradient(270deg, #B22418 1.32%, #FF1200 50.02%, #BA2619 98.71%)",
              color: "#fff",
              fontWeight: "bold",
              fontSize: "1.1rem",
              borderRadius: "10px",
              py: 1.2,
              boxShadow: "0 5px 10px rgba(255, 61, 0, 0.3)",
              "&:hover": {
                background:
                  "linear-gradient(270deg, #BA2619 1.32%, #FF1200 50.02%, #B22418 98.71%)",
              },
            }}
          >
            ยืนยัน
          </Button>
        </Box>
      </Box>


      {/* 🎉 Success Dialog */}
      <Dialog
        open={openSuccess}
        onClose={handleCloseAll}
        TransitionComponent={Transition}
        keepMounted
        PaperProps={{
          sx: {
            borderRadius: "20px",
            textAlign: "center",
            p: 3,
            background:
              "linear-gradient(180deg, #FFF8E1 0%, #FFD54F 100%)",
            boxShadow: "0 0 25px rgba(255, 140, 0, 0.5)",
          },
        }}
      >
        <DialogContent>
          <Box
            component="img"
            src="/success.png"
            alt="success"
            draggable="false"
            sx={{ width: 120, mb: 2 }}
          />
          <Typography variant="h5" fontWeight="bold" color="#D32F2F">
            🎉 ยินดีด้วย! 🎉
          </Typography>
          <Typography mt={1} color="text.secondary">
            คุณได้รับเครดิตฟรี {point} เรียบร้อยแล้ว
          </Typography>

          <Button
            onClick={handleCloseAll}
            sx={{
              mt: 3,
              px: 4,
              borderRadius: "10px",
              background:
                "linear-gradient(90deg, #FF3D00 0%, #FF6D00 100%)",
              color: "#fff",
              fontWeight: "bold",
              "&:hover": {
                background:
                  "linear-gradient(90deg, #FF6D00 0%, #FF3D00 100%)",
              },
            }}
          >
            ปิด
          </Button>
        </DialogContent>
      </Dialog>

      {/* ❌ Error Dialog */}
      <Dialog
        open={openError}
        onClose={handleCloseAll}
        TransitionComponent={Transition}
        keepMounted
        PaperProps={{
          sx: {
            borderRadius: "20px",
            textAlign: "center",
            p: 3,
            background:
              "linear-gradient(180deg, #FFE4E1 0%, #FFCDD2 100%)",
            boxShadow: "0 0 25px rgba(255, 0, 0, 0.4)",
          },
        }}
      >
        <DialogContent>
          <Box
            component="img"
            src="/error.png"
            alt="error"
            draggable="false"
            sx={{ width: 120, mb: 2 }}
          />
          <Typography variant="h5" fontWeight="bold" color="#C62828">
            ❌ ไม่สำเร็จ ❌
          </Typography>
          <Typography mt={1} color="text.secondary">
            {errorMessage ?? "กรุณากรอกข้อมูลให้ครบถ้วนหรือรหัสไม่ถูกต้อง"}
          </Typography>

          <Button
            onClick={handleCloseAll}
            sx={{
              mt: 3,
              px: 4,
              borderRadius: "10px",
              background:
                "linear-gradient(90deg, #D32F2F 0%, #FF5252 100%)",
              color: "#fff",
              fontWeight: "bold",
              "&:hover": {
                background:
                  "linear-gradient(90deg, #FF5252 0%, #D32F2F 100%)",
              },
            }}
          >
            ปิด
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PromoCodeForm;

